// kan-111 round 2 — per-candidate reference questions (D1–D5).
// Opened from the gear button in the AI Reference Taker section header.
// DEFAULT rows: wording locked (admin edits it globally), soft Remove/Restore.
// MANUAL rows: editable label + prompt, hard Delete. Drag-and-drop reorder
// (dnd-kit, same pattern as Pages/ai-reference-questions and the job-order
// Interview Questions tab); keyboard: focus the handle, Space, arrows, Space.
// Save sends the whole list in display order (sortOrder = index).
import { CSSProperties, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import { Button, Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Loader from "../../../components/layout/Loader";
import CLOSE_ICON from "../../../assets/images/close-icon.svg";
import {
  RESTORE_AI_REFERENCE_DEFAULT_QUESTIONS,
  SAVE_AI_REFERENCE_CANDIDATE_QUESTIONS,
} from "./gql/aiReference";
import {
  AiReferenceCandidateQuestionSet,
  QuestionDraft,
  activeQuestionCount,
  newManualDraft,
  toDrafts,
  toSaveQuestions,
  validateDrafts,
} from "./aiReferenceQuestionsUtils";
import "./ReferenceTables.scss";

export const AI_REFERENCE_QUESTIONS_QUERY_NAME = "AiReferenceCandidateQuestions";

const errorMessage = (error: any, fallback: string) =>
  error?.graphQLErrors?.[0]?.message || error?.message || fallback;

// One draggable question row. The drag handle is the order column (grip + index);
// inputs and buttons inside the row are not drag sources, so typing/clicking
// in a manual question never starts a drag.
const SortableQuestionRow = ({
  draft: d,
  index,
  busy,
  onUpdate,
  onRemoveManual,
}: {
  draft: QuestionDraft;
  index: number;
  busy: boolean;
  onUpdate: (key: string, patch: Partial<QuestionDraft>) => void;
  onRemoveManual: (key: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: d.key, disabled: busy });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isDefault = d.source === "DEFAULT";

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`aiRefQRow${d.isRemoved ? " aiRefQRowRemoved" : ""}${
        isDragging ? " aiRefQRowDragging" : ""
      }`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="aiRefQOrder aiRefQHandle"
        aria-label={`Drag to reorder question ${index + 1}`}
        title="Drag to reorder"
        disabled={busy}
        {...attributes}
        {...listeners}
      >
        <span className="aiRefQGrip" aria-hidden="true">
          ⋮⋮
        </span>
        <span className="aiRefQIndex">{index + 1}</span>
      </button>

      <div className="aiRefQBody">
        {isDefault ? (
          <>
            <div className="aiRefQLabel">
              {d.label}
              <span className="aiRefQTag">Default</span>
              {d.isRemoved && (
                <span className="aiRefQTag aiRefQTagMuted">Removed</span>
              )}
            </div>
            <div className="aiRefQPrompt">{d.prompt}</div>
          </>
        ) : (
          <>
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Label (e.g. Leadership)"
              value={d.label}
              disabled={busy}
              maxLength={200}
              onChange={(e) => onUpdate(d.key, { label: e.target.value })}
            />
            <textarea
              className="form-control form-control-sm"
              rows={2}
              placeholder="How would you describe {{candidateName}}'s leadership style?"
              value={d.prompt}
              disabled={busy}
              onChange={(e) => onUpdate(d.key, { prompt: e.target.value })}
            />
          </>
        )}
      </div>

      <div className="aiRefQActions">
        {isDefault ? (
          <button
            type="button"
            className="buttonPrimary aiRefQSmallBtn"
            disabled={busy}
            onClick={() => onUpdate(d.key, { isRemoved: !d.isRemoved })}
          >
            {d.isRemoved ? "Restore" : "Remove"}
          </button>
        ) : (
          <button
            type="button"
            className="buttonPrimary aiRefQSmallBtn aiRefQDanger"
            disabled={busy}
            onClick={() => onRemoveManual(d.key)}
          >
            Delete
          </button>
        )}
      </div>
    </li>
  );
};

const AiReferenceQuestionsModal = ({
  isOpen,
  toggle,
  candidateId,
  questionSet,
  loading,
  loadError,
  forbiddenMessage,
  isForbiddenError,
}: {
  isOpen: boolean;
  toggle: () => void;
  candidateId: string;
  questionSet?: AiReferenceCandidateQuestionSet | null;
  loading?: boolean;
  loadError?: string | null;
  forbiddenMessage: string;
  isForbiddenError: (error: any) => boolean;
}) => {
  const [drafts, setDrafts] = useState<QuestionDraft[]>([]);
  const [confirmRestore, setConfirmRestore] = useState(false);

  const mutationOptions = {
    refetchQueries: [AI_REFERENCE_QUESTIONS_QUERY_NAME],
    awaitRefetchQueries: true,
  };
  const [saveQuestions, { loading: saving }] = useMutation(
    SAVE_AI_REFERENCE_CANDIDATE_QUESTIONS,
    mutationOptions
  );
  const [restoreDefaults, { loading: restoring }] = useMutation(
    RESTORE_AI_REFERENCE_DEFAULT_QUESTIONS,
    mutationOptions
  );
  const busy = saving || restoring;

  // Seed the draft once per opening — as soon as the set is available — so a
  // background refetch never wipes edits in progress.
  const seeded = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      seeded.current = false;
      return;
    }
    if (!seeded.current && questionSet) {
      seeded.current = true;
      setDrafts(toDrafts(questionSet.questions));
      setConfirmRestore(false);
    }
  }, [isOpen, questionSet]);

  const updateDraft = (key: string, patch: Partial<QuestionDraft>) =>
    setDrafts((list) =>
      list.map((d) => (d.key === key ? { ...d, ...patch } : d))
    );

  const removeManual = (key: string) =>
    setDrafts((list) => list.filter((d) => d.key !== key));

  // 5px activation distance so a plain click on the handle never counts as a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setDrafts((list) => {
      const from = list.findIndex((d) => d.key === active.id);
      const to = list.findIndex((d) => d.key === over.id);
      return from < 0 || to < 0 ? list : arrayMove(list, from, to);
    });
  };

  const addQuestion = () => setDrafts((list) => [...list, newManualDraft()]);

  const handleError = (error: any, fallback: string) =>
    toast.error(
      isForbiddenError(error) ? forbiddenMessage : errorMessage(error, fallback)
    );

  const onSave = async () => {
    const problem = validateDrafts(drafts);
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      const res = await saveQuestions({
        variables: {
          input: { candidateId, questions: toSaveQuestions(drafts) },
        },
      });
      if (res?.data?.saveAiReferenceCandidateQuestions) {
        toast.success("Reference questions saved!");
        toggle();
      }
    } catch (error: any) {
      handleError(error, "Could not save the reference questions.");
    }
  };

  const onRestore = async () => {
    try {
      const res = await restoreDefaults({ variables: { candidateId } });
      if (res?.data?.restoreAiReferenceDefaultQuestions) {
        toast.success("Default reference questions restored!");
        toggle();
      }
    } catch (error: any) {
      handleError(error, "Could not restore the default questions.");
    } finally {
      setConfirmRestore(false);
    }
  };

  const activeCount = activeQuestionCount(drafts);

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => !busy && toggle()}
      className="revampDialog revampDialogWidth add-reference-dialog aiRefQuestionsDialog"
      scrollable
    >
      <div className="modalHeaderCustom d-flex align-items-center justify-content-between">
        <h5 className="m-0">Reference Questions</h5>
        <Button
          className="p-0 bg-transparent border-0"
          onClick={toggle}
          disabled={busy}
        >
          <img src={CLOSE_ICON} alt="close" />
        </Button>
      </div>

      <ModalBody className="modalBodyScroll">
        <p className="aiRefQHint">
          Questions Grace asks this candidate's references. Default wording is
          managed by an admin; removed defaults are skipped on the call and left
          off the reference PDF. Use <code>{"{{candidateName}}"}</code> in a
          custom question for the candidate's name.
        </p>

        {loadError && !questionSet ? (
          <p className="text-danger">
            Could not load the questions: {loadError}
          </p>
        ) : loading && !questionSet ? (
          <Loader loaderClass="sm-height" />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={drafts.map((d) => d.key)}
              strategy={verticalListSortingStrategy}
            >
              <ol className="aiRefQList">
                {drafts.map((d, index) => (
                  <SortableQuestionRow
                    key={d.key}
                    draft={d}
                    index={index}
                    busy={busy}
                    onUpdate={updateDraft}
                    onRemoveManual={removeManual}
                  />
                ))}

                {!drafts.length && (
                  <li className="aiRefQEmpty">No questions yet.</li>
                )}
              </ol>
            </SortableContext>
          </DndContext>
        )}

        <button
          type="button"
          className="aiRefQAdd"
          disabled={busy || !questionSet}
          onClick={addQuestion}
        >
          + Add question
        </button>
      </ModalBody>

      <ModalFooter className="pt-3 d-flex justify-content-between">
        <div className="d-flex align-items-center">
          {confirmRestore ? (
            <>
              <span className="aiRefQConfirmText mr-2">
                Discard custom questions and restore the defaults?
              </span>
              <button
                type="button"
                className="buttonGenericStyle filledButton mr-2"
                disabled={busy}
                onClick={onRestore}
              >
                {restoring && <Spinner size="sm" className="mr-2" />}
                Yes, restore
              </button>
              <button
                type="button"
                className="buttonPrimary"
                disabled={busy}
                onClick={() => setConfirmRestore(false)}
              >
                No
              </button>
            </>
          ) : (
            <button
              type="button"
              className="buttonPrimary aiRefQRestoreBtn"
              disabled={busy || !questionSet?.isCustom}
              title={
                questionSet?.isCustom
                  ? undefined
                  : "This candidate already uses the default questions"
              }
              onClick={() => setConfirmRestore(true)}
            >
              Restore defaults
            </button>
          )}
        </div>

        {!confirmRestore && (
          <div className="d-flex align-items-center">
            <span className="aiRefQCount mr-3">
              {activeCount} question{activeCount === 1 ? "" : "s"} asked
            </span>
            <button
              type="button"
              onClick={toggle}
              className="buttonPrimary mr-3"
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="buttonGenericStyle filledButton"
              disabled={busy || !questionSet}
              onClick={onSave}
            >
              {saving && <Spinner size="sm" className="mr-2" />}
              Save
            </button>
          </div>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AiReferenceQuestionsModal;
